import React, {
  useEffect, useContext,
  useState,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import PageWrapper from '../components/pageWrapperHOC';
import {
  getTutorialData, setTutorialData, fetchTutorialData, isLoaderVisible, clearLoader, getLearningMode,
} from '../store/tutorialSlice';
import logger from '../common/logger';
import {
  clearLearningRecords,
  fetchingLearningRecords,
  fetchLearningRecords,
  hasFetchedLearningRecords,
  getUserId,
  getLearningRecords,
  isAuthChecked, getOA2Token,
} from '../store/userSlice';
import EnvContext from '../common/envContext';
import { setErrorState } from '../store/errorSlice';
import {
  ENVIRONMENT,
  ERROR_TYPE_XHR,
  ERROR_TYPE_404,
  REQUEST_FLAGS,
  VERSION, LEARNING_MODE_COURSEWARE,
} from '../common/constants';
import { generateRecordFetchingList } from '../common/utilities';
import TutorialBody from '../components/tutorialBody';
import ModuleBody from '../components/moduleBody';
import {
  formatString,
  triggerGenericPageView,
  triggerModulePageView,
} from '../common/analyticsApi';

const Tutorial = ({ match, location }) => {
  const showLoader = useSelector(isLoaderVisible);
  const envContext = useContext(EnvContext);
  const env = envContext[ENVIRONMENT];
  const { isPreview } = envContext[REQUEST_FLAGS] || false;
  const dispatch = useDispatch();
  const pageData = useSelector(getTutorialData);
  const history = useHistory();
  const isFetchingLearningRecords = useSelector(fetchingLearningRecords);
  const recordChecked = useSelector(hasFetchedLearningRecords);
  const uid = useSelector(getUserId);
  const oa2Token = useSelector(getOA2Token);
  const appVersion = envContext[VERSION];
  const { unitLookup } = pageData;
  const learningMode = useSelector(getLearningMode);
  const [showStatusLoader, setShowStatusLoader] = useState(true);
  const learningRecords = useSelector(getLearningRecords);
  const pageViewCalled = useRef(false);
  const authChecked = useSelector(isAuthChecked);

  useEffect(() => {
    if ((pageData && Object.keys(pageData).length) && location?.pathname?.includes(pageData?.uri) && !pageViewCalled.current && ((authChecked && !uid) || recordChecked) && learningMode) {
      if (learningMode === LEARNING_MODE_COURSEWARE) {
        triggerModulePageView({
          // pageName: `module:${formatString(pageData.title)}`,
          moduleData: pageData,
        });
      } else {
        triggerGenericPageView({
          pageName: `tutorial:${formatString(pageData.title)}`,
          tutorialData: pageData,
          learningRecords,
          env,
        });
      }

      pageViewCalled.current = true;
    }
  }, [pageData, learningRecords, pageViewCalled, authChecked, recordChecked, learningMode]);

  const validateOrFetchData = () => {
    if (unitLookup === undefined || Object.keys(unitLookup).length < 1 || !location?.pathname?.includes(pageData?.uri)) {
      if (unitLookup) { // Old outline data exists - clear it
        dispatch(setTutorialData({}));
        dispatch(clearLearningRecords());
      }
      logger.info('Data is invalid.  Refetching.');
      return dispatch(fetchTutorialData({
        match, env, isPreview,
      })).then((res) => {
        if (res.error) {
          logger.error('ERROR fetching tutorial data: ', match);
          switch (res?.payload?.statusCode) {
          case 500: {
            dispatch(setErrorState({ type: ERROR_TYPE_XHR }));
            break;
          }
          default: {
            dispatch(setErrorState({ type: ERROR_TYPE_404 }));
            break;
          }
          }
        } else {
          const tutorialURI = res?.payload?.uri;
          // If uri returned from data call does not match URI change URI to data result.
          if (!document.location.pathname.endsWith(tutorialURI) && !document.location.hash.endsWith(tutorialURI)) {
            history.replace(tutorialURI);
          }
        }
      });
    }

    // Data is valid clear loader so it can be displayed
    dispatch(clearLoader());
    logger.info('Data is valid.  Continue');
    return Promise.resolve();
  };

  // When path changes validate the data.
  useEffect(() => {
    validateOrFetchData();
  }, [location]);

  useEffect(() => {
    if (unitLookup !== null) {
      if (!isFetchingLearningRecords && uid && !recordChecked) {
        const recordList = generateRecordFetchingList({ tutorialData: pageData, env });
        logger.info('fetching learning records: ', recordList);
        dispatch(fetchLearningRecords({
          uid, recordList, env, appVersion, oa2Token,
        })).then((res) => {
          if (res.error) {
            logger.error('ERROR fetching learning records: ', recordList);
            switch (res?.payload?.statusCode) {
            case 500: {
              dispatch(setErrorState({ type: ERROR_TYPE_XHR }));
              break;
            }
            default: {
              dispatch(setErrorState({ type: ERROR_TYPE_404 }));
              break;
            }
            }
          }
          setShowStatusLoader(false);
        });
      }
    }
  }, [pageData, uid, isFetchingLearningRecords, recordChecked, unitLookup]);

  const title = pageData?.title ? `${pageData.title} | Autodesk` : 'Autodesk';
  return (
    <PageWrapper location={location}>
      <Helmet key="helmet">
        <title>{title}</title>
        <meta property="og:title" content={title} />
        <meta name="twitter:title" content={title} />
      </Helmet>
      { learningMode !== LEARNING_MODE_COURSEWARE && (
        <TutorialBody tutorialData={pageData} showLoader={showLoader} location={location} />
      )}
      { learningMode === LEARNING_MODE_COURSEWARE && (
        <ModuleBody moduleData={pageData} showLoader={showLoader} location={location} showStatusLoader={showStatusLoader} learningRecords={learningRecords} />
      )}

    </PageWrapper>
  );
};

Tutorial.propTypes = {
  match: PropTypes.shape({}).isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired,
};

export default Tutorial;
